import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const resolveWsUrl = ({ token, basePath }) => {
  const explicitWsBase = (process.env.REACT_APP_WS_URL || '').trim().replace(/\/$/, '');
  if (explicitWsBase) {
    return `${explicitWsBase}${basePath}?token=${encodeURIComponent(token)}`;
  }

  const apiUrl = process.env.REACT_APP_API_URL || process.env.REACT_APP_API_URL_DEV || process.env.REACT_APP_API_URL_PROD;
  if (apiUrl && /^https?:\/\//i.test(apiUrl)) {
    const wsBase = apiUrl
      .replace(/^http:/i, 'ws:')
      .replace(/^https:/i, 'wss:')
      .replace(/\/api\/?$/i, '')
      .replace(/\/$/, '');
    return `${wsBase}${basePath}?token=${encodeURIComponent(token)}`;
  }

  const { protocol, hostname, port, host } = window.location;
  const wsProtocol = protocol === 'https:' ? 'wss:' : 'ws:';

  const isLocalHost = hostname === 'localhost' || hostname === '127.0.0.1';
  const isDevPort = port === '3000' || port === '3001';
  const wsHost = isLocalHost || isDevPort ? `${hostname}:8000` : host;

  return `${wsProtocol}//${wsHost}${basePath}?token=${encodeURIComponent(token)}`;
};

export const useMessagingWebSocket = ({
  enabled = true,
  token,
  onMessage,
  onOpen,
  onClose,
  reconnect = true,
  reconnectDelayMs = 1500,
} = {}) => {
  const [status, setStatus] = useState('idle');
  const wsRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const shouldReconnectRef = useRef(false);

  const basePath = useMemo(() => '/ws/messaging/', []);

  const cleanup = useCallback(() => {
    if (reconnectTimerRef.current) {
      window.clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }

    const ws = wsRef.current;
    wsRef.current = null;

    if (ws) {
      try {
        ws.onopen = null;
        ws.onclose = null;
        ws.onerror = null;
        ws.onmessage = null;
        ws.close();
      } catch (_e) {
        // ignore
      }
    }
  }, []);

  const connect = useCallback(() => {
    if (!enabled) return;
    if (!token) return;

    const url = resolveWsUrl({ token, basePath });
    setStatus('connecting');

    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => {
      setStatus('open');
      if (onOpen) onOpen();
    };

    ws.onclose = (event) => {
      setStatus('closed');
      if (onClose) onClose(event);

      if (shouldReconnectRef.current && reconnect) {
        reconnectTimerRef.current = window.setTimeout(() => {
          connect();
        }, reconnectDelayMs);
      }
    };

    ws.onerror = () => {
      setStatus('error');
    };

    ws.onmessage = (event) => {
      if (!event?.data) return;
      try {
        const payload = JSON.parse(event.data);
        if (onMessage) onMessage(payload);
      } catch (_e) {
        // ignore
      }
    };
  }, [basePath, enabled, onClose, onMessage, onOpen, reconnect, reconnectDelayMs, token]);

  useEffect(() => {
    shouldReconnectRef.current = true;
    cleanup();
    connect();

    return () => {
      shouldReconnectRef.current = false;
      cleanup();
    };
  }, [cleanup, connect]);

  const sendJson = useCallback((payload) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return false;
    try {
      ws.send(JSON.stringify(payload));
      return true;
    } catch (_e) {
      return false;
    }
  }, []);

  return { status, wsRef, sendJson };
};
